/**
 * Section « À joindre dans Zoho » (chantier C) : une carte par facture du SCC
 * qu'une dépense Zoho sans pièce peut recevoir, avec la dépense proposée (ou
 * celles à choisir) et le bouton qui joint le fichier. À blanc tant que le
 * réglage « Joindre les pièces dans Zoho » est désactivé : les propositions se
 * lisent, rien n'est envoyé.
 */

import { dateFr, eurosCentimes } from '@scc/core';
import { h, vider } from '../../dom.js';
import {
  categoriserDansZoho,
  comptesCharge,
  depensesPourPiece,
  joindreDansZoho,
  propositionsZoho,
  urlFichierPiece,
  type CompteCharge,
  type DepenseZoho,
  type LigneZoho,
  type PropositionZoho,
  type PropositionsZoho,
} from './api.js';
import { brut, creerRetour, creerSection, lienSur, messageDe, textePaypal, verrouille, type Contexte, type Section } from './communs.js';

const montantFr = (m: number | null, devise: string | null): string =>
  m === null ? 'montant non lu' : devise !== null && devise.toUpperCase() !== 'EUR' ? `${m.toFixed(2).replace('.', ',')} ${devise}` : eurosCentimes(m);

/** Libellé d'une dépense dans une liste de choix. */
export const libelleDepense = (d: DepenseZoho): string =>
  `${dateFr(d.date)} · ${eurosCentimes(d.montant)} · ${d.tiers || d.libelle || 'sans libellé'}${d.titulaire ? ` · ${d.titulaire}` : ''}`;

/** Une ligne de relevé que la facture justifie, encore à catégoriser dans Zoho. */
export function noteACategoriser(lignes: readonly LigneZoho[]): HTMLElement {
  const l = lignes[0] as LigneZoho;
  return h(
    'div',
    { classe: 'note-lecture a-categoriser' },
    h('strong', {}, brut(lignes.length > 1 ? `${String(lignes.length)} lignes de relevé correspondent` : 'Une ligne de relevé correspond')),
    brut(` : ${dateFr(l.date)} · ${eurosCentimes(l.montant)} · ${l.libelle.replace(/\s+/g, ' ')}${l.titulaire ? ` · ${l.titulaire}` : ''}${l.paypal ? ` · ${textePaypal(l.paypal)}` : ''}. `),
    brut('Elle n’est pas encore catégorisée dans Zoho.'),
  );
}

let comptesLus: Promise<readonly CompteCharge[]> | null = null;
/** Comptes de charge du plan comptable, lus une fois pour l'onglet. */
const lireComptes = (): Promise<readonly CompteCharge[]> => {
  comptesLus ??= comptesCharge()
    .then((r) => r.comptes)
    .catch(() => {
      comptesLus = null;
      return [];
    });
  return comptesLus;
};

/** Ce qu'une carte enverra dans Zoho pour une ligne à catégoriser. */
export interface BlocCategoriser {
  readonly el: HTMLElement;
  readonly controles: (HTMLInputElement | HTMLSelectElement)[];
  /** La ligne et le compte choisis ; `null` si rien n'est choisi (ou la case décochée). */
  choix(): { ligne: LigneZoho; compte: string } | null;
}

/**
 * Proposition de catégoriser dans Zoho la ligne de relevé que la facture
 * justifie (décision 55) : la ligne (à choisir s'il y en a plusieurs), le
 * compte de charge proposé par le classement, modifiable. `avecCase` : une case
 * à cocher (carte « À valider ») ; sinon le choix vaut d'office (bouton à part).
 */
export function blocCategoriser(lignes: readonly LigneZoho[], ouvert: boolean, scope: boolean | null, avecCase: boolean, surChangement: () => void = () => undefined): BlocCategoriser {
  const el = h('div', { classe: 'bloc-categoriser' }, noteACategoriser(lignes));
  const case_ = h('input', { type: 'checkbox' });
  const ligne = h('select', { 'aria-label': 'Ligne de relevé à catégoriser' });
  const compte = h('select', { 'aria-label': 'Compte de charge dans Zoho' }, h('option', { value: '' }, 'Compte de charge : à choisir'));
  for (const l of lignes) ligne.appendChild(h('option', { value: l.id }, brut(`${dateFr(l.date)} · ${eurosCentimes(l.montant)} · ${l.libelle.replace(/\s+/g, ' ')}`)));
  ligne.hidden = lignes.length < 2;
  const proposer = (): void => {
    const l = lignes.find((x) => x.id === ligne.value);
    if (l?.compte && [...compte.options].some((o) => o.value === l.compte?.id)) compte.value = l.compte.id;
  };
  if (!ouvert || scope !== true) {
    el.appendChild(
      h(
        'p',
        { classe: 'note-lecture' },
        !ouvert
          ? 'L’envoi dans Zoho n’est pas ouvert : catégorisez la ligne dans Zoho, la jonction sera proposée après l’ingestion suivante.'
          : 'Le jeton Zoho ne permet pas encore de catégoriser depuis l’outil (scope ZohoBooks.banking.CREATE) : catégorisez la ligne dans Zoho, ou renouvelez le jeton.',
      ),
    );
    return { el, controles: [], choix: () => null };
  }
  void lireComptes().then((comptes) => {
    for (const c of comptes) compte.appendChild(h('option', { value: c.id }, brut(c.code ? `${c.code} · ${c.libelle}` : c.libelle)));
    proposer();
    // Une seule ligne et un compte proposé par le classement : la case est cochée d'office.
    case_.checked = lignes.length === 1 && compte.value !== '';
    surChangement();
  });
  ligne.addEventListener('change', () => {
    proposer();
    surChangement();
  });
  compte.addEventListener('change', surChangement);
  case_.addEventListener('change', surChangement);
  el.append(
    avecCase ? h('label', { classe: 'case' }, case_, ' Catégoriser aussi cette ligne dans Zoho et y joindre la facture, au compte :') : h('p', { classe: 'note-lecture' }, 'Catégorisez-la dans Zoho depuis ici, au compte :'),
    ligne,
    compte,
  );
  return {
    el,
    controles: [case_, ligne, compte],
    choix() {
      if (avecCase && !case_.checked) return null;
      const l = lignes.find((x) => x.id === ligne.value);
      return l && compte.value !== '' ? { ligne: l, compte: compte.value } : null;
    },
  };
}

/** La dépense : date, montant, libellé, carte et titulaire, lien vers Zoho. */
export function resumeDepense(d: DepenseZoho): HTMLElement {
  return h(
    'div',
    { classe: 'depense-zoho note-lecture' },
    h('div', {}, h('strong', {}, brut(`${dateFr(d.date)} · ${eurosCentimes(d.montant)}`)), d.tiers ? brut(` · ${d.tiers}`) : null),
    d.libelle ? h('div', { classe: 'note-lecture' }, brut(d.libelle)) : null,
    d.carte === null ? null : h('div', { classe: 'note-lecture' }, brut(`Carte ${d.carte} · ${d.titulaire ?? 'titulaire à renseigner'}`)),
    d.paypal === null ? null : h('div', { classe: 'paypal-ligne' }, brut(textePaypal(d.paypal))),
    d.lienZoho === null ? null : h('div', { classe: 'note-lecture' }, lienSur(d.lienZoho, 'Voir la dépense dans Zoho ↗', 'Ouvrir la dépense dans Zoho Books')),
  );
}

interface Etat {
  /** Joindre est possible : réglage actif et scope accordé. */
  readonly ouvert: boolean;
  /** Pourquoi le bouton est inactif. */
  readonly raison: string;
}

function carteZoho(prop: PropositionZoho, etat: Etat, scopeCategoriser: boolean | null, retirer: () => void, ctx: Contexte): HTMLElement {
  const p = prop.piece;
  const retour = creerRetour();
  // Sans dépense mais avec une ligne de relevé : la catégoriser dans Zoho, puis joindre.
  const blocCat = prop.qualite === 'aucune' && prop.aCategoriser.length > 0 ? blocCategoriser(prop.aCategoriser, etat.ouvert, scopeCategoriser, false) : null;
  const categoriser = h('button', { type: 'button', classe: 'btn-action principal' }, 'Catégoriser dans Zoho et joindre la facture');
  categoriser.hidden = blocCat === null || blocCat.controles.length === 0;
  categoriser.addEventListener('click', () => {
    const c = blocCat?.choix() ?? null;
    if (c === null) {
      retour.ko('Choisissez le compte de charge.');
      return;
    }
    void (async () => {
      try {
        await verrouille([categoriser, ...(blocCat?.controles ?? [])], () => categoriserDansZoho(p.id, c.ligne.id, c.compte));
        ctx.retour.ok(`Ligne du ${dateFr(c.ligne.date)} (${eurosCentimes(c.ligne.montant)}) catégorisée dans Zoho, et « ${p.nom} » jointe à la dépense.`);
        retirer();
      } catch (e) {
        retour.ko(messageDe(e, 'La catégorisation n’a pas abouti.'));
      }
    })();
  });
  const choix = h('select', { 'aria-label': 'Dépense à laquelle joindre la pièce' });
  const zoneDepense = h('div', {});
  const autres = h('button', { type: 'button', classe: 'btn-action' }, prop.qualite === 'aucune' ? 'Choisir une dépense…' : 'Autre dépense…');
  const joindre = h(
    'button',
    { type: 'button', classe: 'btn-action principal', title: etat.ouvert ? 'Envoie le fichier dans Zoho Books, sur cette dépense.' : etat.raison },
    'Joindre à cette dépense',
  );
  joindre.disabled = !etat.ouvert || prop.candidates.length === 0;

  let offertes: readonly DepenseZoho[] = prop.candidates;
  const montrer = (): void => {
    vider(zoneDepense);
    const d = offertes.find((x) => x.id === choix.value);
    if (d) zoneDepense.appendChild(resumeDepense(d));
  };
  const remplir = (liste: readonly DepenseZoho[]): void => {
    offertes = liste;
    vider(choix);
    for (const d of liste) choix.appendChild(h('option', { value: d.id }, brut(libelleDepense(d))));
    choix.hidden = liste.length < 2;
    joindre.disabled = !etat.ouvert || liste.length === 0;
    // Sans dépense offerte, le bouton se cache : « Choisir une dépense… » ou la catégorisation prennent le relais.
    joindre.hidden = liste.length === 0;
    montrer();
  };
  choix.addEventListener('change', montrer);
  remplir(prop.candidates);

  autres.addEventListener('click', () => {
    void (async () => {
      try {
        const { depenses } = await verrouille([autres], () => depensesPourPiece(p.id));
        if (depenses.length === 0) {
          retour.ko('Aucune dépense sans pièce aux dates de cette facture.');
          return;
        }
        retour.effacer();
        remplir(depenses);
        choix.hidden = false;
        autres.hidden = true;
      } catch (e) {
        retour.ko(messageDe(e, 'Les dépenses n’ont pas pu être lues.'));
      }
    })();
  });

  joindre.addEventListener('click', () => {
    const d = offertes.find((x) => x.id === choix.value);
    if (!d) return;
    void (async () => {
      try {
        await verrouille([joindre, autres, choix], () => joindreDansZoho(p.id, d.id));
        ctx.retour.ok(`Pièce « ${p.nom} » jointe dans Zoho à la dépense du ${dateFr(d.date)} (${eurosCentimes(d.montant)}).`);
        retirer();
      } catch (e) {
        retour.ko(messageDe(e, 'La pièce n’a pas pu être jointe.'));
      }
    })();
  });

  const qualite =
    prop.qualite === 'sure'
      ? h('span', { classe: 'badge b-green' }, 'Une seule dépense correspond')
      : prop.qualite === 'a_choisir'
        ? h('span', { classe: 'badge b-amber' }, `${String(prop.candidates.length)} dépenses au même montant : à choisir`)
        : h('span', { classe: 'badge' }, 'Aucune dépense au même montant');

  return h(
    'article',
    { classe: 'carte-piece carte-simple', 'aria-label': `Pièce ${String(p.id)}` },
    h(
      'p',
      {},
      h('strong', {}, brut(p.fournisseur ?? 'Fournisseur non lu')),
      brut(` · ${p.date === null ? 'date non lue' : dateFr(p.date)} · ${montantFr(p.montant, p.devise)}`),
      p.numero ? brut(` · n° ${p.numero}`) : null,
      ' ',
      qualite,
    ),
    h(
      'div',
      { classe: 'note-lecture' },
      brut(p.nom),
      ' · ',
      h('a', { href: urlFichierPiece(p.id), target: '_blank', rel: 'noopener noreferrer' }, 'Ouvrir le fichier ↗'),
      p.etat === 'a_valider' ? brut(' · pas encore déposée dans le Drive') : null,
    ),
    blocCat === null ? null : blocCat.el,
    choix,
    zoneDepense,
    h('div', { classe: 'barre-actions' }, categoriser, joindre, autres),
    retour.el,
  );
}

export interface SectionZoho {
  readonly section: Section;
  charger(): Promise<void>;
}

export function sectionZoho(ctx: Contexte): SectionZoho {
  const etatZone = h('div', {});
  const liste = h('div', { classe: 'liste-cartes' });
  const pied = h('div', {});
  const voirSans = h('button', { type: 'button', classe: 'btn-action' });
  const section = creerSection(5, 'À joindre dans Zoho', 'Chaque facture sur la dépense qu’elle justifie', etatZone);
  section.corps.append(liste, pied);
  let montrerSans = false;
  let dernier: PropositionsZoho | null = null;

  const dessiner = (d: PropositionsZoho): void => {
    dernier = d;
    const etat: Etat = !d.actif
      ? { ouvert: false, raison: 'Pour envoyer, activez le réglage « Joindre les pièces dans Zoho » (Modules › Justificatifs).' }
      : d.scopeEcriture !== true
        ? { ouvert: false, raison: 'Le jeton Zoho n’autorise pas encore l’ajout de pièces : il faut le renouveler avec le scope ZohoBooks.expenses.CREATE.' }
        : { ouvert: true, raison: '' };

    vider(etatZone);
    if (!etat.ouvert) {
      etatZone.appendChild(
        h(
          'div',
          { classe: 'insight warning', role: 'note' },
          h('span', { classe: 'insight-icon', 'aria-hidden': 'true' }, '!'),
          h('p', {}, h('strong', {}, d.actif ? 'Lecture seule : ' : 'Mode à blanc : '), brut(`les rapprochements sont proposés, rien n’est envoyé dans Zoho. ${etat.raison}`)),
        ),
      );
    }
    if (d.scopeEcriture === null) etatZone.appendChild(h('p', { classe: 'note-lecture' }, 'Zoho n’a pas répondu : le droit d’écriture du jeton n’a pas pu être vérifié.'));

    const avec = d.propositions.filter((x) => x.qualite !== 'aucune');
    const sans = d.propositions.filter((x) => x.qualite === 'aucune');
    vider(liste);
    const affichees = montrerSans ? [...avec, ...sans] : avec;
    for (const prop of affichees) {
      const carte: HTMLElement = carteZoho(prop, etat, d.scopeCategoriser, () => {
        carte.remove();
        section.sousTitre(liste.children.length);
      }, ctx);
      liste.appendChild(carte);
    }
    if (affichees.length === 0) liste.appendChild(h('p', { classe: 'note-lecture' }, 'Aucune facture à joindre : chaque pièce reçue a sa dépense, ou attend la catégorisation de sa ligne dans Zoho.'));
    section.sousTitre(avec.length);

    vider(pied);
    if (d.aCategoriser > 0) {
      pied.appendChild(
        h(
          'p',
          { classe: 'note-lecture' },
          `**${String(d.aCategoriser)} facture${d.aCategoriser > 1 ? 's' : ''}** correspond${d.aCategoriser > 1 ? 'ent' : ''} à une ligne de relevé encore à catégoriser dans Zoho : une fois la ligne catégorisée, la dépense sera proposée après l’ingestion suivante.`,
        ),
      );
    }
    if (sans.length > 0) {
      voirSans.textContent = montrerSans ? 'Masquer les factures sans dépense au même montant' : `Afficher aussi les ${String(sans.length)} factures sans dépense au même montant`;
      pied.appendChild(h('div', { classe: 'barre-actions' }, voirSans));
    }
  };

  voirSans.addEventListener('click', () => {
    montrerSans = !montrerSans;
    if (dernier) dessiner(dernier);
  });

  return {
    section,
    async charger() {
      dessiner(await propositionsZoho());
    },
  };
}
